"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";

/**
 * 3D-Bühne im Hintergrund + gestochen scharfer QR-Scanner im Vordergrund.
 */
export default function Preview() {
  const [scanned, setScanned] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setScanned((v) => !v), 2400);
    return () => clearInterval(id);
  }, []);

  const cells = [
    1, 1, 0, 1, 1,
    1, 0, 1, 0, 1,
    0, 1, 1, 1, 0,
    1, 0, 1, 0, 1,
    1, 1, 0, 1, 0,
  ];

  return (
    <div
      aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl p-4 select-none"
      style={{ perspective: "900px" }}
    >
      <motion.div
        animate={{ scale: [1, 1.22, 1], opacity: [0.45, 0.85, 0.45] }}
        transition={{ duration: 4, repeat: Infinity }}
        className="pointer-events-none absolute left-1/2 top-1/2 h-40 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-500/30 blur-3xl"
      />

      <div
        className="pointer-events-none absolute inset-x-[-20%] bottom-[-35%] h-[110%] opacity-45 dark:opacity-60"
        style={{
          transform: "rotateX(68deg)",
          maskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
        }}
      >
        <motion.div
          animate={{ backgroundPosition: ["0px 0px", "0px 28px"] }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="h-full w-full bg-[linear-gradient(to_right,rgba(16,185,129,0.3)_1px,transparent_1px),linear-gradient(to_bottom,rgba(16,185,129,0.3)_1px,transparent_1px)] bg-[size:28px_28px]"
        />
      </div>

      {/* Gestochen scharfer Vordergrund */}
      <div className="relative z-10 flex items-center gap-4 antialiased">
        <motion.div
          animate={{ y: scanned ? -3 : 2 }}
          transition={{ type: "spring", stiffness: 240, damping: 20 }}
          className="relative overflow-hidden rounded-2xl border border-emerald-400/80 bg-white p-3 shadow-[0_18px_36px_-6px_rgba(16,185,129,0.55)] dark:bg-zinc-900"
        >
          <motion.div
            animate={{ top: ["-10%", "110%", "-10%"] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "linear" }}
            className="pointer-events-none absolute inset-x-0 z-20 h-1 bg-gradient-to-r from-transparent via-emerald-400 to-transparent shadow-[0_0_14px_#10b981]"
          />

          <div className="grid grid-cols-5 gap-1">
            {cells.map((active, i) => (
              <span
                key={i}
                className={`h-3 w-3 rounded-[3px] transition-colors duration-300 ${
                  active
                    ? scanned
                      ? "bg-emerald-500 shadow-[0_0_6px_#10b981]"
                      : "bg-neutral-900 dark:bg-white"
                    : "bg-neutral-200/50 dark:bg-zinc-800/50"
                }`}
              />
            ))}
          </div>
        </motion.div>

        <motion.div
          animate={{ y: scanned ? -2 : 2 }}
          className="flex flex-col gap-0.5 rounded-xl border border-emerald-400/70 bg-white px-3 py-2 shadow-xl dark:bg-zinc-900"
        >
          <span className="flex items-center gap-1 font-mono text-[10px] font-black text-emerald-600 dark:text-emerald-400">
            <ShieldCheck className="h-3.5 w-3.5" />
            100% Lokal
          </span>
          <span className="font-sans text-[10px] font-bold text-neutral-500 dark:text-neutral-400">
            Kein Tracking
          </span>
        </motion.div>
      </div>
    </div>
  );
}
